/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

/**
 * Converts a numeric CAN field value into an uppercase hexadecimal string.
 *
 * ```typescript
 * import {toCanNumberHex} from './CanRequestConversionUtils';
 *
 * toCanNumberHex(1824);
 * // '720'
 * ```
 *
 * @param   value The numeric CAN field value.
 * @returns The hexadecimal value for display, or empty/original input.
 */
export const toCanNumberHex = (
  value?: number | string | null
): number | string => {
  const isEmptyValue = value === null || value === undefined || value === '';
  const normalized   = `${value}`.trim();
  const numeric      = Number(normalized);
  const isNumeric    = !Number.isNaN(numeric);

  let result: number | string = normalized;
  if (isEmptyValue) {
    result = '';
  } else if (isNumeric) {
    result = numeric.toString(16).toUpperCase();
  }

  return result;
};

/**
 * Converts comma-delimited decimal CAN payload values to uppercase hex bytes.
 *
 * ```typescript
 * import {toCanPayloadHexDisplay} from './CanRequestConversionUtils';
 *
 * toCanPayloadHexDisplay('34,97,183,0,0,0,0,0');
 * // '22 - 61 - B7 - 00 - 00 - 00 - 00 - 00'
 * ```
 *
 * @param   value A comma-delimited decimal payload string.
 * @returns A hexadecimal payload string for display.
 */
export const toCanPayloadHexDisplay = (value = ''): string => {
  const hasValue      = !!value;
  const isHexLikeView = value.includes('-');

  let result = '';
  if (hasValue && isHexLikeView) {
    result = value;
  } else if (hasValue) {
    result = value
      .split(',')
      .map((token) => {
        const normalized = token.trim();
        const numeric    = Number(normalized);
        const isNumeric  = !Number.isNaN(numeric);
        return isNumeric
          ? numeric.toString(16).toUpperCase().padStart(2, '0')
          : normalized;
      })
      .join(' - ');
  }

  return result;
};

/**
 * Converts a dash-delimited hex payload into a comma-delimited decimal string.
 *
 * ```typescript
 * import {hexPayloadToDecimalCsv} from './CanRequestConversionUtils';
 *
 * hexPayloadToDecimalCsv('0A-1B');
 * // '10,27'
 * ```
 *
 * @param   value The hexadecimal payload value.
 * @returns The comma-delimited decimal payload.
 */
export const hexPayloadToDecimalCsv = (value = ''): string => {
  return value
    .split('-')
    .filter(Boolean)
    .map((token) => parseInt(token, 16))
    .join(',');
};

/**
 * Parses a hexadecimal token into its numeric representation.
 *
 * ```typescript
 * import {hexTokenToNumber} from './CanRequestConversionUtils';
 *
 * hexTokenToNumber('0x1A');
 * // 26
 * ```
 *
 * @param   value The hexadecimal token value.
 * @returns The parsed numeric value.
 */
export const hexTokenToNumber = (value: string): number => {
  const normalized = `${value || ''}`.trim().replace(/^0x/i, '');
  return parseInt(normalized, 16);
};
